<template>
  <ion-modal
    class="modal-big"
    :is-open="isOpen"
    mode="md"
    :backdrop-dismiss="true"
    :show-backdrop="true"
    @didDismiss="dismissModal"
  >
    <modals-layout :data="{ title: modalTitle }" class="layout" @dismiss-modal="dismissModal">
      <div>
        <div class="d-flex justify-content-between">
          <div class="bold">Max of {{ MAX_PROFILE_SELECTION }} folders</div>
          <div>{{ userFeaturedFolderIds?.length || 0 }}/{{ MAX_PROFILE_SELECTION }} selected</div>
        </div>
        <div class="mt-3">
          <div class="d-flex align-items-center">
            <i class="icon pl-2 ti-search position-absolute" />
            <ion-input
              class="c-input one-input"
              :value="searchValue"
              placeholder="Search your folders"
              @input="searchValue = $event.target.value"
            />
          </div>
        </div>
        <div class="mt-2">
          <div class="d-flex justify-content-end">
            <ion-button :color="buttonColor" class="reorder-button" @click="toggleReorder">
              {{ buttonContent }}
            </ion-button>
          </div>

          <Grid scrollable>
            <Sortable
              item-key="id"
              tag="div"
              class="sorting"
              :class="{ dov: isReordering }"
              :list="userFeaturedFolders"
              :options="sortableOptions"
              @end="handleReorder"
            >
              <template #item="{ element: folder, index }">
                <FolderCard
                  :key="index"
                  class="selectable-grid-item"
                  :folder="folder"
                  :is-selected="isFolderSelected(folder)"
                  image-only
                  is-selectable
                  is-removable
                  :is-reorderable="isReordering"
                  @select="select(folder, $event)"
                />
              </template>
            </Sortable>
          </Grid>
        </div>
      </div>

      <div class="main mt-1 d-flex flex-column overflow-auto">
        <div v-if="profileCollections.length" class="selectable-folders mb-3 d-flex align-items-center">
          <Grid :scrollable-on-mobile="false">
            <FolderCard
              v-for="(folder, index) of presentedFolders"
              :key="index"
              class="grid-item"
              :folder="folder"
              is-selectable
              bordered
              :is-selected="isFolderSelected(folder)"
              limited
              @select="select(folder, $event)"
            />
          </Grid>

          <ion-button v-if="hasNextPage" class="clickable" fill="clear" color="dark" @click="loadMoreProfileCollections"
            >Load More →</ion-button
          >
        </div>
        <div v-else class="no-data">No folders</div>
      </div>
      <div class="d-flex mt-1">
        <ion-custom-button
          :disabled="isSaving"
          :loading="isSaving"
          color="primary"
          class="mt-3 mr-2 action-btn"
          @click="save"
          >Save</ion-custom-button
        >
        <ion-custom-button :disabled="isSaving" color="medium" class="mt-3 action-btn" @click="dismissModal"
          >Cancel</ion-custom-button
        >
      </div>
    </modals-layout>
  </ion-modal>
</template>

<script lang="ts" setup>
import { UserProfile, Collection } from 'types/static-types';
import { MAX_PROFILE_SELECTION, socials as _socials } from '@/statics/constants';
import { toast } from '~/native/toast';
import { profileStore } from '~/pinia-store/profile';
import Grid from '@/shared-components/storage/Grid.vue';
import FolderCard from '@/shared-components/storage/FolderCard.vue';
import Sortable from '@/shared-components/Sortable.vue';

const {
  currentProfile,
  loadMoreProfileCollections,
  profileCollectionsPaging,
  profileCollections,
  updateUserProfile,
  isSaving,
} = profileStore();

defineProps({
  isOpen: {
    type: Boolean,
  },
  modalTitle: {
    type: String,
    default: 'Select featured folders',
  },
});

const handleReorder = (e: any) => {
  const { oldIndex, newIndex } = e;
  const element = userFeaturedFolderIds.value[oldIndex];
  userFeaturedFolderIds.value.splice(oldIndex, 1);
  userFeaturedFolderIds.value.splice(newIndex, 0, element);
};

const searchValue = ref('');

const sortableOptions = ref({
  handle: '.reorder-handle',
  animation: 150,
});

const hasNextPage = computed(() => {
  return !!profileCollectionsPaging.value?.next;
});

const emits = defineEmits(['dismissModal']);

const dismissModal = () => {
  emits('dismissModal');
};

const isFolderSelected = (folder: Collection) => {
  return userFeaturedFolderIds.value.includes(folder?.id!);
};

const toggleReorder = () => {
  isReordering.value = !isReordering.value;

  if (!isReordering.value) {
    const data: Partial<UserProfile> = {
      featured: {
        ...currentProfile.value?.featured,
        collections: userFeaturedFolderIds.value,
      },
    };
    updateUserProfile(data);
  }
};

const presentedFolders = computed(() =>
  searchValue.value
    ? profileCollections.value.filter((folder) => folder.slug?.toLocaleLowerCase().includes(searchValue.value))
    : profileCollections.value
);

const select = (folder: Collection, isSelected: boolean) => {
  if (isSelected && userFeaturedFolderIds.value.length > MAX_PROFILE_SELECTION - 1) {
    toast.show('Maximum selected', 'nonative', 'warning');
    return;
  }

  if (isSelected) {
    userFeaturedFolderIds.value.push(folder.id!);
    userFeaturedFolderIds.value = [...new Set(userFeaturedFolderIds.value)];

    userFeaturedFolders.value.push(folder);
    userFeaturedFolders.value = [...new Set(userFeaturedFolders.value)];
  }

  if (!isSelected) {
    userFeaturedFolderIds.value = userFeaturedFolderIds.value.filter((folderId) => folderId !== folder.id);
    userFeaturedFolders.value = userFeaturedFolders.value.filter(({ id }) => id !== folder.id);
  }
};

const userFeaturedFolderIds = ref([]);
const userFeaturedFolders = ref<Collection[]>([]);
const isReordering = ref(false);

const buttonContent = computed(() => (isReordering.value ? 'Save order' : 'Reorder'));
const buttonColor = computed(() => (isReordering.value ? 'success' : 'primary'));

const save = () => {
  const data: Partial<UserProfile> = {
    featured: {
      ...currentProfile.value?.featured,
      collections: userFeaturedFolderIds.value,
    },
  };
  updateUserProfile(data);
  dismissModal();
};

onMounted(() => {
  userFeaturedFolderIds.value = currentProfile.value?.featured?.collections || [];
  userFeaturedFolders.value = currentProfile.value?.featured_collections || [];
});

watch(currentProfile, (val) => {
  userFeaturedFolderIds.value = val?.featured?.collections || [];
  userFeaturedFolders.value = val?.featured_collections || [];
});
</script>

<style lang="sass" scoped>
.sorting
  grid-gap: 6px
  display: flex
  &.dov
    overflow: hidden
.reorder-button
  text-transform: unset
  --border-radius: 6px
.selectable-grid-item
  width: 60px !important
  min-width: 60px !important
  &.sortable-chosen
    opacity: 0.7
.icon
  z-index: 22

.modal-big
  font-size: 14px
.main
  height: calc(100% - 164px - 2rem - 55px)
  overflow-x: hidden !important

.modal-big
  --width: 616px
  --height: 80%


@media(max-width: 768px)
  .modal-big
    --width: 100%
    --height: 100%
.c-input
  ::v-deep
    input
      padding-left: 30px !important

.action-btn
  width: 90px !important
  margin-top: 0 !important
  ::v-deep
    *
      text-transform: unset !important
    .button
      height: 40px !important
</style>
